import React, { useEffect, useState } from "react";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Autocomplete,
} from "@mui/material";
import { searchParamsToString } from "src/helper/publicFunction";
import { LocationOn, Search } from "@mui/icons-material";
import AddressAutocomplete from "src/pages/orders/order-components/update-order-components/shipper-autocomplete";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import {
  createOrder,
  getCustomers,
  quickQuoteData,
  getAccessorials,
} from "src/queries/query";
import { toast } from "react-toastify";
import GenericModal from "src/components/generic-modal";
import AddCustomer from "../orders/order-components/AddCustomer";
import { useQuote } from "src/contexts/QuoteContext";
import CustomerSelectionModal from "../orders/order-components/CustomerSelectionModal";
import { useNavigate } from "react-router";

const validationSchema = Yup.object({
  dimWeight: Yup.number()
    .required("Weight is required")
    .positive("Weight must be positive")
    .min(1, "Weight must be at least 1 lb"),
  origin: Yup.object({
    name: Yup.string().required("Origin is required"),
    quickSearch: Yup.string().required("Origin address is required"),
  }),
  destination: Yup.object({
    name: Yup.string().required("Destination is required"),
    quickSearch: Yup.string().required("Destination address is required"),
  }),
});

const initialValues = {
  dimWeight: "",
  quantity: "",
  weight: "",
  accessorials: [],
  width: "",
  height: "",
  lengthValue: "",
  carrier: {},
  origin: {
    name: "",
    quickSearch: "",
    shortCode: "",
    address: "",
    city: "",
    state: "",
    postal: "",
    country: "",
    latitude: "",
    longitude: "",
    contactName: "",
    contactPhone: "",
    contactEmail: "",
  },
  destination: {
    name: "",
    quickSearch: "",
    shortCode: "",
    address: "",
    city: "",
    state: "",
    postal: "",
    country: "",
    latitude: "",
    longitude: "",
    contactName: "",
    contactPhone: "",
    contactEmail: "",
  },
  accessorialUuids: [],
};

// Form Component that uses Formik context
const QuickQuoteForm = ({
  handleNestedChange,
  handleAddressChange,
  customers,
  isSubmitting: externalIsSubmitting,
  quotes,
  error,
  setFieldValue: externalSetFieldValue,
}) => {
  const {
    values,
    setFieldValue: contextSetFieldValue,
    isSubmitting = externalIsSubmitting,
    handleChange,
    handleBlur,
    touched = {},
    errors = {},
    status,
  } = useFormikContext();

  const { data: accessorialsData } = useQuery({
    queryKey: ["accessorials"],
    queryFn: () => getAccessorials(),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  // Use the external setFieldValue if provided, otherwise use the one from context
  const setFieldValue = externalSetFieldValue || contextSetFieldValue;

  // Calculate dimensional weight
  const calculateDimWeight = () => {
    const qty = Number.parseFloat(values.quantity) || 0;
    const lg = Number.parseFloat(values.lengthValue) || 0;
    const wd = Number.parseFloat(values.width) || 0;
    const ht = Number.parseFloat(values.height) || 0;

    if (qty && lg && wd && ht) {
      const calculatedWeight = Math.round((qty * lg * wd * ht) / 172.8);
      if (calculatedWeight) {
        setFieldValue("dimWeight", calculatedWeight);
      }
    }
  };

  useEffect(() => {
    calculateDimWeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.quantity, values.lengthValue, values.width, values.height]);

  return (
    <Form>
      <Box
        sx={{
          backgroundColor: "white",
          py: 2,
          px: 2,
          borderRadius: "0px 0px 8px 8px",
          bgcolor: "#fff",
          border: "1px solid #e0e0e0",
        }}
      >
        {/* Status messages */}
        {status?.error && (
          <Box sx={{ mb: 2 }}>
            <Typography color="error">{status.error}</Typography>
          </Box>
        )}
        {status?.success && (
          <Box sx={{ mb: 2 }}>
            <Typography color="success.main">{status.success}</Typography>
          </Box>
        )}

        {/* DIM Weight Calculator Section */}
        <Box sx={{ mb: 3 }}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="start"
            gap={1}
            sx={{ mb: 2 }}
          >
            <Typography variant="body3" fontWeight="600">
              DIM Weight Calculator
            </Typography>
            <Typography variant="subtitle1" fontSize={12} color="#666">
              Please fill the dimensions to calculate the DIM Weight{" "}
              <span style={{ color: "red" }}>*</span>
            </Typography>
          </Box>

          {/* Dimension Fields */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Quantity */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                name="quantity"
                label="Quantity"
                variant="outlined"
                type="number"
                value={values.quantity}
                size="small"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.quantity && Boolean(errors.quantity)}
                helperText={touched.quantity && errors.quantity}
              />
            </Grid>

            {/* Width */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                name="width"
                label="Width"
                variant="outlined"
                type="number"
                value={values.width}
                size="small"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.width && Boolean(errors.width)}
                helperText={touched.width && errors.width}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">in</InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Height */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                name="height"
                label="Height"
                variant="outlined"
                type="number"
                value={values.height}
                size="small"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.height && Boolean(errors.height)}
                helperText={touched.height && errors.height}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">in</InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Length */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                name="lengthValue"
                label="Length"
                variant="outlined"
                type="number"
                value={values.lengthValue}
                size="small"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.lengthValue && Boolean(errors.lengthValue)}
                helperText={touched.lengthValue && errors.lengthValue}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">in</InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          {/* Dimensional Weight Field */}
          <Box sx={{ mb: 0 }}>
            <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
              <Typography variant="body2" fontWeight="600">
                Shipment DIM Weight
              </Typography>
            </Box>
            <TextField
              fullWidth
              name="dimWeight"
              placeholder="Dim Weight"
              variant="outlined"
              type="number"
              value={values.dimWeight}
              size="small"
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.dimWeight && Boolean(errors.dimWeight)}
              helperText={touched.dimWeight && errors.dimWeight}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">lbs</InputAdornment>
                ),
                readOnly: true, // Make it read-only since it's calculated
              }}
            />
          </Box>
        </Box>

        {/* Origin Section */}
        <Box sx={{ mb: 2 }}>
          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
            <LocationOn sx={{ fontSize: "16px", color: "grey.400" }} />
            <Typography variant="body2" fontWeight="600">
              Origin
            </Typography>
          </Box>

          <AddressAutocomplete
            customers={customers}
            values={values}
            handleNestedChange={handleNestedChange(setFieldValue)}
            handleAddressChange={handleAddressChange(setFieldValue)}
            setFieldValue={setFieldValue}
            fieldName="origin"
            onBlur={handleBlur}
            error={
              (touched.origin?.quickSearch || touched.origin?.name) &&
              (Boolean(errors.origin?.quickSearch) ||
                Boolean(errors.origin?.name))
            }
            helperText={
              (touched.origin?.quickSearch && errors.origin?.quickSearch) ||
              (touched.origin?.name && errors.origin?.name) ||
              null
            }
          />

          {/* Display selected origin details */}
          {/* {values.origin.city && (
            <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography
                color="text.secondary"
                gutterBottom
                fontWeight="bold"
                fontSize="12px"
              >
                Selected Origin:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {values.origin.address && `${values.origin.address}, `}
                {values.origin.city && `${values.origin.city}, `}
                {values.origin.state} {values.origin.postal}
              </Typography>
              {values.origin.country && (
                <Typography variant="body2" color="text.secondary">
                  {values.origin.country}
                </Typography>
              )}
            </Box>
          )} */}
        </Box>

        {/* Destination Section */}
        <Box sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
            <LocationOn sx={{ fontSize: "16px", color: "grey.400" }} />
            <Typography variant="body2" fontWeight="600">
              Destination
            </Typography>
          </Box>

          <AddressAutocomplete
            customers={customers}
            values={values}
            handleNestedChange={handleNestedChange(setFieldValue)}
            handleAddressChange={handleAddressChange(setFieldValue)}
            setFieldValue={setFieldValue}
            fieldName="destination"
            onBlur={handleBlur}
            error={
              (touched.destination?.quickSearch || touched.destination?.name) &&
              (Boolean(errors.destination?.quickSearch) ||
                Boolean(errors.destination?.name))
            }
            helperText={
              (touched.destination?.quickSearch &&
                errors.destination?.quickSearch) ||
              (touched.destination?.name && errors.destination?.name) ||
              null
            }
          />

          {/* Display selected destination details */}
          {/* {values.destination.city && (
            <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography
                color="text.secondary"
                gutterBottom
                fontWeight="bold"
                fontSize="12px"
              >
                Selected Destination:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {values.destination.address &&
                  `${values.destination.address}, `}
                {values.destination.city && `${values.destination.city}, `}
                {values.destination.state} {values.destination.postal}
              </Typography>
              {values.destination.country && (
                <Typography variant="body2" color="text.secondary">
                  {values.destination.country}
                </Typography>
              )}
            </Box>
          )} */}
        </Box>
        {/* Accessorials */}
        <Box sx={{ mb: 2 }}>
          <Autocomplete
            multiple
            variant="outlined"
            size="small"
            options={accessorialsData || []}
            getOptionLabel={(opt) => opt.name}
            value={
              accessorialsData?.filter((o) =>
                values?.accessorialUuids?.includes(o.uuid)
              ) || []
            }
            onChange={(_, newSelection) => {
              setFieldValue(
                "accessorialUuids",
                newSelection.map((s) => s.uuid)
              );
            }}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  label={option.name}
                  {...getTagProps({ index })}
                  key={option.uuid}
                  size="small"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Add accessorials "
                variant="outlined"
                size="small"
              />
            )}
            sx={{ mt: 1 }}
          />
        </Box>

        {/* Search Button */}
        <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isSubmitting}
            startIcon={<Search />}
            fullWidth
            sx={{ minWidth: 140 }}
          >
            {isSubmitting ? "Searching..." : "Get Quote"}
          </Button>
        </Box>
      </Box>
    </Form>
  );
};

export default function QuickQuotePage() {
  useEffect(() => {
    document.title = "Quick Quote";
  }, []);

  const [isCreateCustomerModalOpen, setIsCreateCustomerModalOpen] =
    useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [subTotalAmount, setSubTotalAmount] = useState(0);
  const [searchParams, setSearchParams] = useState({
    searchQuery: "",
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setQuoteData } = useQuote();

  const { data: customersData, isLoading: isLoadingCustomers } = useQuery({
    queryKey: ["customers", searchParamsToString(searchParams)],
    queryFn: () =>
      getCustomers({
        params: searchParamsToString(searchParams),
      }),
    staleTime: Infinity,
  });
  const customers = customersData?.data; // You can pass your customers data here
  const {
    mutate: submitQuoteRequest,
    data: quoteResponse,
    isPending: isSubmitting,
  } = useMutation({
    mutationFn: (values) => {
      const payload = {
        weight: parseFloat(values.dimWeight),
        origin: values?.origin.city,
        destination: values?.destination.city,
        accessorialUuids: values?.accessorialUuids,
      };
      return quickQuoteData(payload);
    },
    onError: (error) => {
      console.error("Error fetching quotes:", error);
    },
  });

  const quotes = quoteResponse?.data?.response || null;
  const carriers = quoteResponse?.data?.carrierResult || null;
  const sortedCarriers = carriers?.sort((a, b) => a.tariffCost - b.tariffCost);
  const error = quoteResponse?.data?.response?.error;

  const handleNestedChange =
    (setFieldValue) => (fieldName, nestedField, value) => {
      setFieldValue(`${fieldName}.${nestedField}`, value);
    };

  const handleAddressChange =
    (setFieldValue) => (fieldName, addressField, value) => {
      setFieldValue(`${fieldName}.${addressField}`, value);
    };

  const handleAddNewCustomer = () => {
    setIsCreateModalOpen(false);
    setIsCreateCustomerModalOpen(true);
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
  };

  const handleCreateOrder = async (values) => {
    if (selectedCustomer) {
      const orderData = {
        billingParty: {
          name: selectedCustomer.name,
          shortCode: selectedCustomer.shortCode,
          customerType: selectedCustomer.customerType || "shipper",
          billingOption: selectedCustomer.billingOption || "shipper",
          requireQuote: selectedCustomer.requireQuote || false,
          currency: selectedCustomer.currency || "CAD",
          isActive: selectedCustomer.isActive,
          addresses: {
            city: selectedCustomer.addresses?.city || "",
            state: selectedCustomer.addresses?.state || "",
            postal: selectedCustomer.addresses?.postal || "",
            address: selectedCustomer.addresses?.address1 || "",
            country: selectedCustomer.addresses?.country || "",
            latitude: selectedCustomer.addresses?.latitude,
            longitude: selectedCustomer.addresses?.longitude,
          },
        },
      };
      const result = await createOrder(orderData);
      if (result?.status === 200) {
        toast.success("Order created successfully.");
        setQuoteData(values);
        navigate(`/order/new/${result?.data?.data?.uuid}`);
      } else {
        toast.error("Cannot create order.");
      }
    } else {
      toast.error("Please select a customer first");
    }
  };

  const handleSubmit = async (
    values,
    { setSubmitting, setStatus, setTouched, setFieldError, setErrors }
  ) => {
    try {
      // Mark all fields as touched to show validation errors
      const touchedFields = {};

      // Mark top-level fields as touched
      Object.keys(initialValues).forEach((key) => {
        touchedFields[key] = true;

        // Handle nested objects like origin and destination
        if (
          typeof initialValues[key] === "object" &&
          initialValues[key] !== null
        ) {
          touchedFields[key] = {};
          Object.keys(initialValues[key]).forEach((nestedKey) => {
            touchedFields[key][nestedKey] = true;
          });
        }
      });

      setTouched(touchedFields, false); // Don't validate on touch

      // Validate the form
      await validationSchema.validate(values, { abortEarly: false });

      // If validation passes, submit the form
      submitQuoteRequest(values);
      // Save the quote data to context
      toast.success("Quote requested successfully!");
    } catch (error) {
      console.error("Error submitting form:", error);
      if (error.name === "ValidationError") {
        // Handle validation errors
        const errors = {};
        error.inner.forEach((err) => {
          // Handle nested field errors (e.g., origin.quickSearch)
          const path = err.path.split(".");
          if (path.length > 1) {
            const [parent, child] = path;
            if (!errors[parent]) errors[parent] = {};
            errors[parent][child] = err.message;
          } else {
            errors[path[0]] = err.message;
          }
        });
        setErrors(errors);
        setStatus({ error: "Please fill in all required fields" });
      } else {
        setStatus({ error: error.message || "Failed to submit quote" });
      }
    } finally {
      setSubmitting(false);
      setQuoteData(values);
    }
  };

  useEffect(() => {
    if (quotes) {
      const accessorialTotal = quotes?.accessorialCosts?.total || 0;
      const quoteTotal = quotes?.total || 0;

      setSubTotalAmount(quoteTotal - accessorialTotal);
      setTotalAmount(quoteTotal);
    }
  }, [quotes]);

  useEffect(() => {
    if (selectedCarrier) {
      // Calculate carrier cost (base rate + fuel surcharge)
      const baseRate = Number(selectedCarrier?.tariffCost || 0);
      const fuelSurcharge =
        (baseRate * Number(selectedCarrier?.fuelRate || 0)) / 100;
      const carrierCost = baseRate + fuelSurcharge;

      // Calculate new total based on the original quote total minus carrier cost
      const quoteTotal = quotes?.total || 0;
      setTotalAmount(quoteTotal - carrierCost);
    } else {
      // If no carrier is selected, show the full quote amount
      setTotalAmount(quotes?.total || 0);
    }
  }, [selectedCarrier, quotes]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnBlur={true}
      validateOnChange={false}
      validateOnMount={false}
    >
      {(formikProps) => (
        <>
          <div
            className="flex flex-col md:flex-row h-screen p-4 gap-4 overflow-auto"
            style={{ backgroundColor: "white" }}
          >
            <div className="w-full md:w-1/4 h-full ">
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  bgcolor: "#3f4b63",
                  color: "white",
                  borderRadius: "8px 8px 0 0",
                }}
              >
                <Typography variant="h6">Search Quotes</Typography>
              </Box>

              <QuickQuoteForm
                formikProps={formikProps}
                handleNestedChange={handleNestedChange}
                handleAddressChange={handleAddressChange}
                customers={customers}
                isSubmitting={isSubmitting}
                quotes={quotes}
                error={error}
                setFieldValue={formikProps.setFieldValue}
              />
              <GenericModal
                open={isCreateModalOpen}
                onClose={() => {
                  setIsCreateModalOpen(false);
                }}
                title="Create a new order"
                primaryButtonText="Create order"
                secondaryButtonText="Cancel"
                onPrimaryAction={() => handleCreateOrder(formikProps.values)}
                maxWidth="sm"
                fullWidth={true}
                desktopWidth="700px"
                isLoading={isLoadingCustomers}
              >
                <CustomerSelectionModal
                  customers={customers}
                  onSelectCustomer={handleSelectCustomer}
                  onAddNewCustomer={handleAddNewCustomer}
                  setSearchParams={setSearchParams}
                  isLoading={isLoadingCustomers}
                />
              </GenericModal>
              <GenericModal
                open={isCreateCustomerModalOpen}
                onClose={(e) => {
                  e?.stopPropagation();
                  setIsCreateCustomerModalOpen(false);
                }}
                title="Create Customer"
                primaryButtonText="Add"
                secondaryButtonText="Cancel"
                onPrimaryAction={(e) => {
                  e?.stopPropagation();
                  const form = document.querySelector("#customer-add-form");
                  if (form) {
                    const formik = form.closest("form");
                    if (formik) {
                      formik.requestSubmit?.();
                      const submitButton = formik.querySelector(
                        'button[type="submit"]'
                      );
                      if (submitButton) {
                        submitButton.click();
                      }
                    }
                  }
                }}
                maxWidth="sm"
                fullWidth={true}
                desktopWidth="500px"
              >
                <AddCustomer
                  createFromQuote={true}
                  onClose={() => {
                    queryClient.invalidateQueries({
                      queryKey: ["customers"],
                    });
                    setIsCreateCustomerModalOpen(false);
                    toast.success("Customer added successfully!");
                  }}
                />
              </GenericModal>
            </div>
            <div className="w-full md:w-3/4 h-full flex flex-col md:flex-row">
              <div className="w-full">
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: "#3f4b63",
                    color: "white",
                    borderRadius: "8px 8px 0 0",
                  }}
                >
                  <Typography variant="h6">Quotes</Typography>
                </Box>
                <div className="w-full flex flex-col md:flex-row border border-gray-200 rounded-lg rounded-t-none">
                  <Box
                    sx={{
                      height: "100%",
                      width: "25%",
                    }}
                  >
                    <Box
                      sx={{
                        py: 1.1,
                        px: 2,
                        backgroundColor: "#F5F5F5",
                        borderBottom: "1px solid #e0e0e0",
                        borderRadius: 0,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: "bold",
                          textTransform: "uppercase",
                          fontSize: "13px",
                          letterSpacing: 0.5,
                          color: "text.primary",
                        }}
                      >
                        Available Carriers
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "11px",
                          color: "#858585",
                          display: "block",
                        }}
                      >
                        Sorted by lowest cost
                      </Typography>
                    </Box>

                    {isSubmitting ? (
                      <Box
                        sx={{
                          p: 4,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          minHeight: 120, // ensures space for visibility
                        }}
                      >
                        <CircularProgress size={32} thickness={4} />
                      </Box>
                    ) : sortedCarriers?.length > 0 ? (
                      sortedCarriers.map((carrier, index) => {
                        // Define color scheme based on index (everything after 3 is high)
                        const colorIndex = index > 2 ? 2 : index;
                        const colors = {
                          0: {
                            border: "#4caf50",
                            badge: "#4caf50",
                            bg: "rgba(76, 175, 80, 0.08)",
                          }, // Green
                          1: {
                            border: "#ffa726",
                            badge: "#ffa726",
                            bg: "rgba(255, 167, 38, 0.08)",
                          }, // Light Orange-Yellow
                          2: {
                            border: "#ff5722",
                            badge: "#ff5722",
                            bg: "rgba(255, 87, 34, 0.08)",
                          }, // Red-Orange
                        };
                        const currentColor = colors[colorIndex];

                        return (
                          <Box
                            key={carrier.carrierUuid}
                            sx={{
                              position: "relative",
                              p: 1,
                              borderRadius: 0,
                              borderBottom:
                                "1px solid rgba(224, 224, 224, 0.5)",
                              cursor: "pointer",
                              backgroundColor:
                                selectedCarrier?.carrierUuid ===
                                carrier.carrierUuid
                                  ? currentColor.bg
                                  : "transparent",
                              borderLeft:
                                selectedCarrier?.carrierUuid ===
                                carrier.carrierUuid
                                  ? `25px solid ${currentColor.border}`
                                  : `3px solid ${currentColor.border}`,
                              transition:
                                "border-left 0.3s ease-in-out, background-color 0.3s ease-in-out",
                              "&:hover": {
                                backgroundColor: "rgba(224, 224, 224, 0.5)",
                              },
                            }}
                            onClick={() => {
                              if (
                                selectedCarrier?.carrierUuid ===
                                carrier.carrierUuid
                              ) {
                                // Deselect carrier
                                const newCarrier = null;
                                setSelectedCarrier(newCarrier);
                                formikProps.setFieldValue(
                                  "carrier",
                                  newCarrier
                                );

                                // Update quoteData with carrier removed
                                const updatedQuoteData = {
                                  ...formikProps.values,
                                  carrier: newCarrier,
                                  carrierCost: 0,
                                };
                                setQuoteData(updatedQuoteData);
                              } else {
                                // Select new carrier
                                setSelectedCarrier(carrier);
                                formikProps.setFieldValue("carrier", carrier);

                                // Calculate carrier cost (base rate + fuel surcharge)
                                const baseRate = Number(
                                  carrier?.tariffCost || 0
                                );
                                // Assuming fuelSurcharge is also part of the carrier object or calculated elsewhere
                                // For now, let's just ensure tariffCost is in quoteData
                                const updatedQuoteData = {
                                  ...formikProps.values,
                                  carrier: { ...carrier, tariffCost: baseRate }, // Ensure tariffCost is included
                                };
                                setQuoteData(updatedQuoteData);
                              }
                            }}
                          >
                            {/* Header Row - Name and Badge */}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  // color: currentColor.border,
                                }}
                              >
                                {carrier.carrierName}
                              </Typography>
                              <Box
                                sx={{
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1,
                                  backgroundColor: currentColor.badge,
                                  color: "white",
                                  fontSize: "0.65rem",
                                  fontWeight: 500,
                                }}
                              >
                                {colorIndex === 0
                                  ? "Best"
                                  : colorIndex === 1
                                  ? "Good"
                                  : "High"}
                              </Box>
                            </Box>

                            {/* Cost Row */}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 0.5,
                              }}
                            >
                              <Typography variant="body2" color="textSecondary">
                                Cost:
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: currentColor.border,
                                }}
                              >
                                $
                                {(
                                  carrier.tariffCost +
                                  (carrier?.fuelRate * carrier.tariffCost) / 100
                                ).toFixed(2) || "N/A"}
                              </Typography>
                            </Box>

                            {/* Check Icon in Border for Selected Carrier */}
                            {selectedCarrier?.uuid === carrier.uuid && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: "50%",
                                  left: -19,
                                  transform: "translateY(-50%)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "white",
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                  zIndex: 1,
                                }}
                              >
                                ✓
                              </Box>
                            )}
                          </Box>
                        );
                      })
                    ) : (
                      <Box
                        sx={{
                          p: 4,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          minHeight: "100%", // ensures space for visibility
                        }}
                      >
                        <Typography variant="body2" color="textSecondary">
                          No carriers available
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Quote Table */}
                  <Table
                    sx={{
                      height: "100%",
                      borderRadius: "0px 0px 8px 8px",
                      bgcolor: "#fff",
                      borderLeft: "1px solid #e0e0e0",
                      width: "75%",
                    }}
                  >
                    <TableContainer>
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: "#f5f5f5",
                                textTransform: "uppercase",
                              }}
                            >
                              Type
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: "#f5f5f5",
                                textTransform: "uppercase",
                              }}
                            >
                              Description
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: "#f5f5f5",
                              }}
                            >
                              QTY / %
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: "#f5f5f5",
                              }}
                            >
                              RATE
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: "#f5f5f5",
                              }}
                            >
                              COST $
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody sx={{ mt: 1 }}>
                          {isSubmitting ? (
                            <TableRow>
                              <TableCell colSpan={5}>
                                <Box sx={{ p: 3, textAlign: "center" }}>
                                  <CircularProgress color="primary" />
                                </Box>
                              </TableCell>
                            </TableRow>
                          ) : !error && quotes ? (
                            <>
                              {/* Freight Row */}
                              {quotes.freight && (
                                <TableRow>
                                  <TableCell>Freight</TableCell>
                                  <TableCell>Base freight cost</TableCell>
                                  <TableCell>
                                    {quotes.freight.percentage}%
                                  </TableCell>
                                  <TableCell>
                                    {(quotes.freight.weight / 100).toFixed(2)}
                                  </TableCell>
                                  <TableCell>${quotes.freight.rate}</TableCell>
                                </TableRow>
                              )}

                              {/* Fuel Row */}
                              {quotes.fuel && (
                                <TableRow>
                                  <TableCell>Fuel Surcharge</TableCell>
                                  <TableCell>Fuel surcharge</TableCell>
                                  <TableCell>
                                    {quotes.fuel.percentage}%
                                  </TableCell>
                                  <TableCell>{quotes.freight.rate}</TableCell>
                                  <TableCell>
                                    ${quotes.fuel.rate.toFixed(2)}
                                  </TableCell>
                                </TableRow>
                              )}

                              {/* Accessorial Costs Row */}
                              {quotes.accessorialCosts &&
                                quotes.accessorialCosts.total > 0 && (
                                  <TableRow>
                                    <TableCell>Accessorial</TableCell>
                                    <TableCell>Additional services</TableCell>
                                    <TableCell>100%</TableCell>
                                    <TableCell>
                                      ${quotes.accessorialCosts.total}
                                    </TableCell>
                                    <TableCell>
                                      $
                                      {quotes.accessorialCosts.total.toFixed(2)}
                                    </TableCell>
                                  </TableRow>
                                )}

                              {/* Accessorial Details Row */}
                              <TableRow>
                                <TableCell>Accessorial Details</TableCell>
                                <TableCell colSpan={4}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 0.5,
                                    }}
                                  >
                                    {quotes.accessorialCosts?.accessorialsObj &&
                                    Object.keys(
                                      quotes.accessorialCosts.accessorialsObj
                                    ).length > 0 ? (
                                      Object.entries(
                                        quotes.accessorialCosts.accessorialsObj
                                      ).map(([accessorial, price], index) => (
                                        <Chip
                                          key={index}
                                          label={`${accessorial}: $${price}`}
                                          size="small"
                                          color="primary"
                                          variant="outlined"
                                        />
                                      ))
                                    ) : (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        No accessorial charges
                                      </Typography>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>

                              {/* Total Row */}

                              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                                <TableCell colSpan={6} sx={{ py: 2 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 1,
                                      maxWidth: 210,
                                      ml: "auto", // Align right
                                    }}
                                  >
                                    {/* Subtotal */}
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                      }}
                                    >
                                      <Typography
                                        variant="subtitle1"
                                        fontWeight="bold"
                                      >
                                        Quote Amount:
                                      </Typography>
                                      <Typography
                                        variant="subtitle1"
                                        fontWeight="bold"
                                      >
                                        ${subTotalAmount.toFixed(2)}
                                      </Typography>
                                    </Box>
                                    {/* Accessorials */}
                                    {quotes?.accessorialCosts?.total > 0 && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          color: "primary.main",
                                        }}
                                      >
                                        <Typography
                                          variant="subtitle1"
                                          fontWeight="bold"
                                        >
                                          Accessorials:
                                        </Typography>
                                        <Typography
                                          variant="subtitle1"
                                          fontWeight="bold"
                                        >
                                          $
                                          {quotes.accessorialCosts.total.toFixed(
                                            2
                                          )}
                                        </Typography>
                                      </Box>
                                    )}
                                    {/* Carrier cost */}
                                    {selectedCarrier && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          color: "error.main",
                                        }}
                                      >
                                        <Typography
                                          variant="subtitle1"
                                          fontWeight="bold"
                                        >
                                          Carrier Cost:
                                        </Typography>
                                        <Typography
                                          variant="subtitle1"
                                          fontWeight="bold"
                                        >
                                          -$
                                          {(
                                            (selectedCarrier?.tariffCost *
                                              selectedCarrier?.fuelRate) /
                                              100 +
                                            selectedCarrier?.tariffCost
                                          ).toFixed(2)}
                                        </Typography>
                                      </Box>
                                    )}

                                    {/* Divider */}
                                    <Box
                                      sx={{
                                        borderTop:
                                          "1px solid rgba(224, 224, 224, 1)",
                                        pt: 1,
                                      }}
                                    />

                                    {/* Total */}
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        color:
                                          totalAmount < 0
                                            ? "error.main"
                                            : totalAmount > 0
                                            ? "success.main"
                                            : "text.primary",
                                      }}
                                    >
                                      <Typography
                                        variant="subtitle1"
                                        fontWeight="bold"
                                      >
                                        Total:
                                      </Typography>
                                      <Typography
                                        variant="subtitle1"
                                        fontWeight="bold"
                                      >
                                        ${totalAmount.toFixed(2)}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            </>
                          ) : (
                            <TableRow>
                              <TableCell colSpan={5}>
                                <Box sx={{ p: 3, textAlign: "center" }}>
                                  <Typography
                                    variant="body2"
                                    color={error ? "#f44336" : "text.secondary"}
                                  >
                                    {error
                                      ? error
                                      : `Enter shipment details and click "Get Quote"
                                    to see results`}
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Table>
                </div>
                {(quotes?.totalCosts?.toFixed(2) ||
                  quotes?.total?.toFixed(2)) && (
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      sx={{ minWidth: 140 }}
                      onClick={() => setIsCreateModalOpen(true)}
                    >
                      Create order with this quote
                    </Button>
                  </Box>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </Formik>
  );
}
